import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/stores/authStore';
import * as authApi from '../auth.api';
import { toAuthUser } from '../auth-user.mapper';
import { landingPath } from '@/lib/landing';

export function useAuth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { i18n } = useTranslation();
  const { setUser, setAuth, logout: clearAuth } = useAuthStore();

  const { accessToken } = useAuthStore();
  const { isLoading: isCheckingAuth } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const response = await authApi.getCurrentUser();
      const user = toAuthUser(response.data);
      setUser(user);
      return user;
    },
    retry: false,
    staleTime: 5 * 60 * 1000,
    enabled: !!accessToken,
  });

  const loginMutation = useMutation({
    mutationFn: authApi.login,
    onSuccess: (response) => {
      const user = toAuthUser(response.data.user);
      // spec-simulatie-stopt-bij-uitloggen: een nieuwe login erft niets van een sessie die nog liep
      // (inlogscherm via browser-terug): eerst uitloggen stopt de simulatie en wist de lijststand.
      clearAuth();
      setAuth(user, response.data.accessToken);
      queryClient.invalidateQueries({ queryKey: ['auth'] });

      // Restore user's language preference
      if (user.language) {
        i18n.changeLanguage(user.language);
      }

      // First login: force password change
      if (!user.hasChangedPassword) {
        navigate('/change-password');
        return;
      }

      // Redirect based on role or return URL
      const redirect = searchParams.get('redirect');
      if (redirect) {
        navigate(redirect);
      } else {
        navigate(landingPath(user.role));
      }
    },
  });

  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSuccess: () => {
      clearAuth();
      queryClient.clear();
      navigate('/login');
    },
    onError: () => {
      clearAuth();
      queryClient.clear();
      navigate('/login');
    },
  });

  const forgotPasswordMutation = useMutation({
    mutationFn: authApi.forgotPassword,
  });

  const resetPasswordMutation = useMutation({
    mutationFn: authApi.resetPassword,
    onSuccess: () => {
      navigate('/login?resetSuccess=true');
    },
  });

  // No navigation here: this mutation is reused both by the forced first-login flow
  // (ChangePasswordPage, which DOES want a redirect afterwards) and by the "Mijn gegevens"
  // ChangePasswordCard (h12-3, which wants the caller to stay on the page and see the
  // inline success message). Each caller decides its own post-success navigation via the
  // per-call `mutate(vars, { onSuccess })` option instead of a hardcoded redirect here.
  const changePasswordMutation = useMutation({
    mutationFn: authApi.changePassword,
    onSuccess: () => {
      // Update user state to reflect password changed
      const currentUser = useAuthStore.getState().user;
      if (currentUser) {
        setUser({ ...currentUser, hasChangedPassword: true });
      }
    },
  });

  return {
    isCheckingAuth,
    loginMutation,
    logoutMutation,
    forgotPasswordMutation,
    resetPasswordMutation,
    changePasswordMutation,
  };
}
